import React, { useState, useCallback, useMemo, useRef } from 'react';
import { Button, Form, Input, Select, Switch, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import GenderSelect from 'components/form/GenderSelect';
import useGetSettingData from 'hooks/useGetSettingData';
import useLoadBasicData from 'hooks/useLoadBasicData';
import { EnumFields, SettingEnum } from 'types/enumerationData';
import klarityLogo from 'assets/logo/KlarityLogo.png';
import AIChat from 'components/AIChat';
import { EAIChatPageId } from 'types/ai';
import { inviteProviders, switchProvider, sendTeamMemberInvite } from 'api/group';
import { saveKlarityServiceSpecialty, savePPAPaymentMethod } from 'api/ppaOnboarding';
import SwitchedProviderContext from 'pages/ReviewProviderProfilePage/SwitchedProviderContext';
import { PATH } from 'constants/path';
import { openManageSubscriptionModal } from 'utils/globalLayerControl';
import StepProgressBar from './components/StepProgressBar';
import SetupServicesStep from './components/SetupServicesStep';
import PaymentMethodStep from './components/PaymentMethodStep';
import ServiceTemplateStep, { ServiceTemplateStepRef } from './components/ServiceTemplateStep';
import WorkingHoursStep, { WorkingHoursStepRef } from './components/WorkingHoursStep';
import AdvanceNoticeStep, { AdvanceNoticeStepRef } from './components/AdvanceNoticeStep';
import EnrichProfileStep, { EnrichProfileStepRef } from './components/EnrichProfileStep';
import s from './s.module.less';

enum EPagePhase {
    BASIC_INFO,
    STEP_1_SPECIALTY,
    STEP_1_PAYMENT,
    STEP_1_SERVICE,
    STEP_2_HOURS,
    STEP_2_NOTICE,
    STEP_3,
}

const STEP_MAP: Record<number, number> = {
    [EPagePhase.STEP_1_SPECIALTY]: 1,
    [EPagePhase.STEP_1_PAYMENT]: 1,
    [EPagePhase.STEP_1_SERVICE]: 1,
    [EPagePhase.STEP_2_HOURS]: 2,
    [EPagePhase.STEP_2_NOTICE]: 2,
    [EPagePhase.STEP_3]: 3,
};

const PAGE_ID_MAP: Partial<Record<EPagePhase, EAIChatPageId>> = {
    [EPagePhase.STEP_1_SPECIALTY]: EAIChatPageId.GROUP_PROVIDER_SETUP_SPECIALTY,
    [EPagePhase.STEP_1_PAYMENT]: EAIChatPageId.GROUP_PROVIDER_PAYMENT_METHOD,
    [EPagePhase.STEP_1_SERVICE]: EAIChatPageId.GROUP_PROVIDER_SERVICE_TEMPLATE,
    [EPagePhase.STEP_2_HOURS]: EAIChatPageId.GROUP_PROVIDER_WORKING_HOURS,
    [EPagePhase.STEP_2_NOTICE]: EAIChatPageId.GROUP_PROVIDER_ADVANCE_NOTICE,
    [EPagePhase.STEP_3]: EAIChatPageId.GROUP_PROVIDER_ENRICH_PROFILE,
};

// One entry per provider created from the basic-info form; the wizard runs through them in order.
interface CreatedProvider {
    providerId: number;
    name: string;
}

let cardSeq = 0;
const nextCardKey = () => {
    cardSeq += 1;
    return `p${cardSeq}`;
};

const AddGroupProviderPage: React.FC = () => {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [phase, setPhase] = useState<EPagePhase>(EPagePhase.BASIC_INFO);
    const [loadSettingData] = useLoadBasicData();
    const serviceStepRef = useRef<ServiceTemplateStepRef>(null);
    const hoursStepRef = useRef<WorkingHoursStepRef>(null);
    const noticeStepRef = useRef<AdvanceNoticeStepRef>(null);
    const enrichStepRef = useRef<EnrichProfileStepRef>(null);

    React.useEffect(() => {
        loadSettingData();
    }, [loadSettingData]);

    const [credentialTypes] = useGetSettingData(EnumFields.CREDENTIAL_TYPE);
    const [cardKeys, setCardKeys] = useState<string[]>(() => [nextCardKey()]);
    const [adminMap, setAdminMap] = useState<Record<string, boolean>>({});
    const savedValuesRef = useRef<any>(null);
    const [selectedSpecialty, setSelectedSpecialty] = useState<string>('');
    const [paymentMethod, setPaymentMethod] = useState<string>('');
    const [submitting, setSubmitting] = useState(false);
    // populated after BASIC_INFO: the created provider queue and the wizard's position in it
    const [createdProviders, setCreatedProviders] = useState<CreatedProvider[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [currentTeamMemberId, setCurrentTeamMemberId] = useState<number | null>(null);
    const [switchedToken, setSwitchedToken] = useState<string | undefined>(undefined);

    // Restore the basic-info form when navigating back to the first step.
    React.useEffect(() => {
        if (phase === EPagePhase.BASIC_INFO && savedValuesRef.current) {
            form.setFieldsValue(savedValuesRef.current);
        }
    }, [phase, form]);

    const isWizard = phase !== EPagePhase.BASIC_INFO;
    const currentStep = STEP_MAP[phase] || 1;
    const currentProvider = createdProviders[currentIndex];
    const isLast = currentIndex >= createdProviders.length - 1;
    // Once providers are created the card list is locked: re-entering BASIC_INFO is view-only for add/remove.
    const locked = createdProviders.length > 0;

    const handleExit = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    const handleAddCard = useCallback(() => {
        setCardKeys((prev) => [...prev, nextCardKey()]);
    }, []);

    const handleRemoveCard = useCallback((key: string) => {
        setCardKeys((prev) => prev.filter((k) => k !== key));
        setAdminMap((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
    }, []);

    const handleBack = useCallback(() => {
        if (phase === EPagePhase.BASIC_INFO) {
            navigate(-1);
        } else if (phase === EPagePhase.STEP_1_SPECIALTY) {
            // Only the first provider can return to the basic-info form; for later
            // providers the earlier invitations are already sent, so Back is hidden.
            if (currentIndex === 0) {
                setPhase(EPagePhase.BASIC_INFO);
            }
        } else if (phase === EPagePhase.STEP_1_PAYMENT) {
            setPhase(EPagePhase.STEP_1_SPECIALTY);
        } else if (phase === EPagePhase.STEP_1_SERVICE) {
            setPhase(EPagePhase.STEP_1_PAYMENT);
        } else if (phase === EPagePhase.STEP_2_HOURS) {
            setPhase(EPagePhase.STEP_1_SERVICE);
        } else if (phase === EPagePhase.STEP_2_NOTICE) {
            setPhase(EPagePhase.STEP_2_HOURS);
        } else if (phase === EPagePhase.STEP_3) {
            setPhase(EPagePhase.STEP_2_NOTICE);
        }
    }, [phase, currentIndex, navigate]);

    // Switch identity to the provider at `index` and restart the wizard for them.
    const switchToProvider = useCallback(async (index: number, queue: CreatedProvider[]) => {
        const target = queue[index];
        if (!target) return false;
        const tokenRes = await switchProvider(target.providerId);
        const loginData = tokenRes?.data?.data;
        if (tokenRes?.data?.code !== 200 || !loginData?.token) {
            message.error(tokenRes?.data?.message || 'Failed to switch provider');
            return false;
        }
        setSwitchedToken(loginData.token);
        setCurrentTeamMemberId(loginData.teamMemberId ?? null);
        setCurrentIndex(index);
        setSelectedSpecialty('');
        setPaymentMethod('');
        setPhase(EPagePhase.STEP_1_SPECIALTY);
        return true;
    }, []);

    // BASIC_INFO: create all providers in one batch (sendInvite:false), then switch to the first.
    const handleBasicInfoNext = useCallback(async () => {
        let values: any;
        try {
            values = await form.validateFields();
        } catch {
            return; // validation failed
        }
        savedValuesRef.current = values;

        // Providers are created only once; on re-entry to this step just advance.
        if (createdProviders.length) {
            setPhase(EPagePhase.STEP_1_SPECIALTY);
            return;
        }

        const providersPayload = cardKeys.map((key) => {
            const v = values.providers?.[key] || {};
            return {
                email: v.email,
                firstName: v.firstName,
                lastName: v.lastName,
                credential: (v.credential || []).join(','),
                gender: v.gender,
                isAdmin: !!adminMap[key],
            };
        });

        setSubmitting(true);
        try {
            const inviteRes = await inviteProviders({
                providers: providersPayload,
                sendInvite: false,
            });
            const results = inviteRes?.data?.data?.results;
            if (inviteRes?.data?.code !== 200 || !results?.length) {
                message.error(inviteRes?.data?.message || 'Failed to create providers');
                return;
            }
            const failed = results.find((r: any) => r.errorMessage || !r.providerId);
            if (failed) {
                message.error(failed.errorMessage || 'Failed to create providers');
                return;
            }
            const queue: CreatedProvider[] = results.map((r: any, i: number) => ({
                providerId: r.providerId,
                name: `${providersPayload[i].firstName} ${providersPayload[i].lastName}`.trim(),
            }));
            setCreatedProviders(queue);
            await switchToProvider(0, queue);
        } catch (error) {
            console.error(error);
            message.error('Failed to create providers');
        } finally {
            setSubmitting(false);
        }
    }, [form, cardKeys, adminMap, createdProviders, switchToProvider]);

    // STEP_1_SPECIALTY: persist the chosen specialty to the current provider, then advance.
    const handleSpecialtyNext = useCallback(async () => {
        if (!selectedSpecialty) {
            message.error('Please select a specialty');
            return;
        }
        setSubmitting(true);
        try {
            const res = await saveKlarityServiceSpecialty(
                { recordType: 'KLARITY_ONBOARDING_SPECIALTY', value: selectedSpecialty },
                switchedToken,
            );
            if (res?.error) {
                message.error(res.error || 'Failed to save specialty');
                return;
            }
            setPhase(EPagePhase.STEP_1_PAYMENT);
        } catch (error) {
            console.error(error);
            message.error('Failed to save specialty');
        } finally {
            setSubmitting(false);
        }
    }, [selectedSpecialty, switchedToken]);

    // STEP_1_PAYMENT: persist the chosen payment method to the current provider, then advance.
    const handlePaymentNext = useCallback(async () => {
        if (!paymentMethod) {
            message.error('Please select a payment method');
            return;
        }
        setSubmitting(true);
        try {
            const res = await savePPAPaymentMethod(paymentMethod, switchedToken);
            if (res?.error) {
                message.error(res.error || 'Failed to save payment method');
                return;
            }
            setPhase(EPagePhase.STEP_1_SERVICE);
        } catch (error) {
            console.error(error);
            message.error('Failed to save payment method');
        } finally {
            setSubmitting(false);
        }
    }, [paymentMethod, switchedToken]);

    // STEP_3: send the invitation using the original (admin) token, then either
    // move to the next queued provider or finish.
    const handleSendInvite = useCallback(async () => {
        if (!currentTeamMemberId) {
            navigate(-1);
            return;
        }
        setSubmitting(true);
        try {
            // Persist the (edited) basic info to the current provider before sending the invitation.
            const saved = await enrichStepRef.current?.submit();
            if (saved === false) {
                return;
            }

            const res = await sendTeamMemberInvite(currentTeamMemberId);
            if (res?.data?.code !== 200) {
                message.error(res?.data?.message || 'Failed to send invitation');
                return;
            }
            message.success('Invitation sent');

            if (isLast) {
                // Return to Settings > Team Management so the user can continue adding members.
                navigate(`${PATH.DASHBOARD}`);
                openManageSubscriptionModal('9');
            } else {
                await switchToProvider(currentIndex + 1, createdProviders);
            }
        } catch (error) {
            console.error(error);
            message.error('Failed to send invitation');
        } finally {
            setSubmitting(false);
        }
    }, [currentTeamMemberId, isLast, currentIndex, createdProviders, switchToProvider, navigate]);

    // Run a step component's ref.submit() (which persists with the switched token) then advance.
    const runStepSubmit = useCallback(
        async (stepRef: React.RefObject<{ submit: () => Promise<boolean> }>, nextPhase: EPagePhase) => {
            if (!stepRef.current) {
                setPhase(nextPhase);
                return;
            }
            setSubmitting(true);
            try {
                const ok = await stepRef.current.submit();
                if (ok) {
                    setPhase(nextPhase);
                }
            } finally {
                setSubmitting(false);
            }
        },
        [],
    );

    const handleNext = useCallback(() => {
        switch (phase) {
            case EPagePhase.BASIC_INFO:
                handleBasicInfoNext();
                break;
            case EPagePhase.STEP_1_SPECIALTY:
                handleSpecialtyNext();
                break;
            case EPagePhase.STEP_1_PAYMENT:
                handlePaymentNext();
                break;
            case EPagePhase.STEP_1_SERVICE:
                runStepSubmit(serviceStepRef, EPagePhase.STEP_2_HOURS);
                break;
            case EPagePhase.STEP_2_HOURS:
                runStepSubmit(hoursStepRef, EPagePhase.STEP_2_NOTICE);
                break;
            case EPagePhase.STEP_2_NOTICE:
                runStepSubmit(noticeStepRef, EPagePhase.STEP_3);
                break;
            case EPagePhase.STEP_3:
                handleSendInvite();
                break;
            default:
                break;
        }
    }, [phase, handleBasicInfoNext, handleSpecialtyNext, handlePaymentNext, handleSendInvite, runStepSubmit]);

    const handleSkip = useCallback(() => {
        if (phase === EPagePhase.STEP_1_SERVICE) {
            setPhase(EPagePhase.STEP_2_HOURS);
        } else if (phase === EPagePhase.STEP_2_HOURS) {
            setPhase(EPagePhase.STEP_2_NOTICE);
        } else if (phase === EPagePhase.STEP_2_NOTICE) {
            setPhase(EPagePhase.STEP_3);
        }
    }, [phase]);

    const providerName = currentProvider?.name || '';

    const switchedProviderValue = useMemo(
        () => ({ scheduleToken: switchedToken, providerId: currentProvider?.providerId }),
        [switchedToken, currentProvider],
    );

    const renderBasicInfo = () => (
        <Form form={form} layout="vertical">
            {cardKeys.map((key, idx) => (
                <div key={key} className={s.providerSection}>
                    <div className={s.providerHeader}>
                        <span className={s.providerLabel}>Provider {idx + 1}</span>
                        {!locked && cardKeys.length > 1 && (
                            <span className={s.removeLink} onClick={() => handleRemoveCard(key)}>
                                Remove
                            </span>
                        )}
                    </div>
                    <div className={s.basicInfoCard}>
                        <div className={s.formRow}>
                            <Form.Item
                                label="First name"
                                name={['providers', key, 'firstName']}
                                className="flex-1"
                                rules={[{ required: true, message: 'Please enter first name' }]}
                            >
                                <Input placeholder="First name" />
                            </Form.Item>
                            <Form.Item
                                label="Last name"
                                name={['providers', key, 'lastName']}
                                className="flex-1"
                                rules={[{ required: true, message: 'Please enter last name' }]}
                            >
                                <Input placeholder="Last name" />
                            </Form.Item>
                        </div>
                        <div className={s.formRow}>
                            <Form.Item
                                label="Credentials"
                                name={['providers', key, 'credential']}
                                className="flex-1"
                                rules={[{ required: true, message: 'Please select credentials' }]}
                            >
                                <Select
                                    mode="multiple"
                                    showArrow
                                    fieldNames={{ label: 'content', value: 'dictKey' }}
                                    options={credentialTypes as SettingEnum[]}
                                    placeholder="Select credentials"
                                />
                            </Form.Item>
                            <Form.Item
                                label="Gender"
                                name={['providers', key, 'gender']}
                                className="flex-1"
                                rules={[{ required: true, message: 'Please select gender' }]}
                            >
                                <GenderSelect placeholder="Select gender" />
                            </Form.Item>
                        </div>
                        <Form.Item
                            label="Email"
                            name={['providers', key, 'email']}
                            rules={[
                                { required: true, message: 'Please enter email' },
                                { type: 'email', message: 'Please enter a valid email' },
                                {
                                    validator: (_, value) => {
                                        if (!value) return Promise.resolve();
                                        const dup = cardKeys.some(
                                            (k) => k !== key && form.getFieldValue(['providers', k, 'email']) === value,
                                        );
                                        return dup
                                            ? Promise.reject(
                                                  new Error('This email is already used for another provider'),
                                              )
                                            : Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <Input placeholder="Email address" />
                        </Form.Item>
                        <div className={s.toggleRow}>
                            <Switch
                                checked={!!adminMap[key]}
                                onChange={(v) => setAdminMap((prev) => ({ ...prev, [key]: v }))}
                                className={s.toggleSwitch}
                            />
                            <div className={s.toggleContent}>
                                <div className={s.toggleTitle}>Practice-wide admin access</div>
                                <div className={s.toggleDesc}>
                                    Grants access to system configuration, user management, billing oversight, and
                                    practice-level settings. Overrides role-based permissions.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ))}
            {!locked && (
                <span className={s.addProviderLink} onClick={handleAddCard}>
                    Add another provider
                </span>
            )}
        </Form>
    );

    const renderWizardContent = () => {
        switch (phase) {
            case EPagePhase.STEP_1_SPECIALTY:
                return <SetupServicesStep value={selectedSpecialty} onSpecialtyChange={setSelectedSpecialty} />;
            case EPagePhase.STEP_1_PAYMENT:
                return <PaymentMethodStep value={paymentMethod} onChange={setPaymentMethod} />;
            case EPagePhase.STEP_1_SERVICE:
                return (
                    <ServiceTemplateStep
                        ref={serviceStepRef}
                        paymentMethod={paymentMethod}
                        selectedSpecialty={selectedSpecialty}
                    />
                );
            case EPagePhase.STEP_2_HOURS:
                return <WorkingHoursStep ref={hoursStepRef} />;
            case EPagePhase.STEP_2_NOTICE:
                return <AdvanceNoticeStep ref={noticeStepRef} />;
            case EPagePhase.STEP_3:
                return <EnrichProfileStep ref={enrichStepRef} />;
            default:
                return null;
        }
    };

    const renderFooter = () => {
        if (phase === EPagePhase.BASIC_INFO) {
            return (
                <>
                    <Button className={s.backBtn} onClick={handleBack}>
                        Back
                    </Button>
                    <Button type="primary" className={s.continueBtn} onClick={handleNext} loading={submitting}>
                        Next
                    </Button>
                </>
            );
        }

        // For providers after the first, earlier invitations are already sent, so the
        // specialty step (the wizard's first step) offers no way back.
        const hideBack = phase === EPagePhase.STEP_1_SPECIALTY && currentIndex > 0;

        if (phase === EPagePhase.STEP_3) {
            return (
                <>
                    <Button className={s.backBtn} onClick={handleBack}>
                        Back
                    </Button>
                    <Button type="primary" className={s.continueBtn} onClick={handleNext} loading={submitting}>
                        {isLast ? 'Send invitation and finish' : 'Send invitation and set up next provider'}
                    </Button>
                </>
            );
        }

        // Specialty and payment are required and cannot be skipped.
        const canSkip =
            phase === EPagePhase.STEP_1_SERVICE ||
            phase === EPagePhase.STEP_2_HOURS ||
            phase === EPagePhase.STEP_2_NOTICE;

        return (
            <>
                {!hideBack && (
                    <Button className={s.backBtn} onClick={handleBack}>
                        Back
                    </Button>
                )}
                <Button type="primary" className={s.continueBtn} onClick={handleNext} loading={submitting}>
                    Next
                </Button>
                {canSkip && (
                    <Button type="link" className={s.skipBtn} onClick={handleSkip}>
                        Skip for now
                    </Button>
                )}
            </>
        );
    };

    return (
        <div className={s.page}>
            <div className={s.topBar}>
                <img src={klarityLogo} alt="Klarity" className={s.logo} />
                <Button className={s.exitBtn} onClick={handleExit}>
                    Exit
                </Button>
            </div>

            <div className={s.contentArea}>
                <div className={s.mainContent}>
                    {isWizard && (
                        <>
                            <div className={s.infoBanner}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M18 10C18 14.4183 14.4183 18 10 18C5.58172 18 2 14.4183 2 10C2 5.58172 5.58172 2 10 2C14.4183 2 18 5.58172 18 10ZM11 6C11 6.55228 10.5523 7 10 7C9.44772 7 9 6.55228 9 6C9 5.44772 9.44772 5 10 5C10.5523 5 11 5.44772 11 6ZM9 9C8.44772 9 8 9.44772 8 10C8 10.5523 8.44772 11 9 11V14C9 14.5523 9.44772 15 10 15H11C11.5523 15 12 14.5523 12 14C12 13.4477 11.5523 13 11 13V10C11 9.44772 10.5523 9 10 9H9Z"
                                        fill="#60A5FA"
                                    />
                                </svg>
                                <span>
                                    You are currently setting up <strong>{providerName}</strong>&apos;s profile
                                </span>
                            </div>
                            <StepProgressBar currentStep={currentStep} />
                        </>
                    )}

                    {phase === EPagePhase.BASIC_INFO ? (
                        <>
                            <h2 className={s.title}>Add provider</h2>
                            {renderBasicInfo()}
                        </>
                    ) : (
                        /* Expose the current (switched) provider so both the wizard steps and the
                           shared components they reuse (e.g. InsuranceTable, FormCard) operate as
                           that provider. Keyed by providerId so every step remounts per provider. */
                        <SwitchedProviderContext.Provider value={switchedProviderValue}>
                            <div className={s.wizardContent} key={currentProvider?.providerId}>
                                {renderWizardContent()}
                            </div>
                        </SwitchedProviderContext.Provider>
                    )}

                    <div className={s.footer}>{renderFooter()}</div>
                </div>

                {isWizard && (
                    <div className={s.sidebarArea}>
                        <AIChat defaultShow pageId={PAGE_ID_MAP[phase]} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default AddGroupProviderPage;
